import merge from 'lodash/merge';

import type { PartialTtsData } from '../../types';
import type { CacheInstance } from '../Cache';
import { Cache } from '../cache.platform';

/**
 * A simple cache store for TTS word boundaries.
 * Keyed by `{documentId}_{voice}`.
 * Used by Bookwise to persist word boundaries across app restarts.
 */
class TtsWordBoundariesStore {
  private _cache: CacheInstance;

  constructor() {
    this._cache = Cache.createInstance({ name: 'store__ttsWordBoundaries' });
  }

  async get(documentId: string, voice: string): Promise<PartialTtsData[string] | null> {
    const key = this.makeKey(documentId, voice);
    const result = await this._cache.getItem<PartialTtsData[string]>(key);
    return result;
  }

  async merge(documentId: string, voice: string, data: PartialTtsData[string]): Promise<PartialTtsData[string]> {
    const existing = await this.get(documentId, voice);
    const merged: PartialTtsData[string] = merge({}, existing ?? {}, data);
    await this._cache.setItem(this.makeKey(documentId, voice), merged);
    return merged;
  }

  async clearCache(): Promise<void> {
    await this._cache.clear();
  }

  private makeKey(documentId: string, voice: string): string {
    return `${documentId}_${voice}`;
  }
}

export default TtsWordBoundariesStore;
